import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CardLogo } from "@/components/CardLogo";
import { Link } from "@/lib/router-compat";
import { trackAffiliateClick } from "@/lib/analytics";
import { trackAffiliateClickGTM } from "@/lib/gtm";
import { useAffiliateRedirect } from "@/hooks/useAffiliateRedirect";
import { annualFeeLabel, cardHighlights, categoryLine } from "@/lib/cardFacts";
import { formatPercentDE, showsCreditMetrics } from "@/lib/cardCategory";

interface HomepageComparisonTableProps {
  cards: any[];
  offers: Map<string, any>;
  sponsoredIds: Set<string>;
}

const repaymentExample = (card: any): string | null => {
  if (!showsCreditMetrics(card) || card?.cost_example_validated !== true) return null;
  if (typeof card?.apr_effective !== "number") return null;
  return `Teilzahlung: ${formatPercentDE(card.apr_effective)} effektiver Jahreszins`;
};

export function HomepageComparisonTable({ cards, offers, sponsoredIds }: HomepageComparisonTableProps) {
  const { handleAffiliateRedirect } = useAffiliateRedirect();
  if (cards.length === 0) return null;

  return (
    <section aria-labelledby="direktvergleich-heading" className="border-y border-border bg-muted/40">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-6 max-w-3xl">
          <h2 id="direktvergleich-heading" className="text-2xl font-bold text-foreground sm:text-3xl">
            Kreditkarten im direkten Vergleich
          </h2>
          <p className="mt-2 text-muted-foreground">
            Die wichtigsten Kosten und Merkmale der zuerst gelisteten Karten auf einen Blick.
          </p>
        </div>

        <div className="overflow-x-auto border-y border-border bg-card">
          <table className="w-full min-w-[880px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="w-[290px] px-4 py-3 font-semibold">Karte</th>
                <th className="px-4 py-3 font-semibold">Jahresgebühr</th>
                <th className="px-4 py-3 font-semibold">Kartentyp / Zins</th>
                <th className="w-[280px] px-4 py-3 font-semibold">Merkmale</th>
                <th className="w-[180px] px-4 py-3 text-center font-semibold">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {cards.slice(0, 6).map((card, index) => {
                const offer = offers.get(card.id);
                const affiliateUrl = offer?.apply_url || card.affiliate_link;
                const example = repaymentExample(card);
                const highlights = cardHighlights(card, 3);
                const hasApplicableApr = showsCreditMetrics(card) && typeof card.apr_effective === "number" && card.apr_effective > 0;
                const rate = hasApplicableApr
                  ? `${formatPercentDE(card.apr_effective)} eff. p. a.`
                  : categoryLine(card).label;

                return (
                  <tr key={card.id} className="border-b border-border last:border-b-0 align-top">
                    <td className="px-4 py-4">
                      <Link to={`/kreditkarten/${card.slug}`} className="flex items-center gap-3 text-foreground hover:text-primary">
                        <CardLogo
                          logoUrl={card.logo_url}
                          bankName={card.display_name || card.name}
                          className="h-12 w-[76px] shrink-0 object-contain"
                          width={76}
                          height={48}
                          priority={index < 2}
                        />
                        <span className="min-w-0">
                          <span className="block font-semibold leading-snug">{card.display_name || card.name}</span>
                          <span className="mt-0.5 block text-xs text-muted-foreground">{card.issuer}</span>
                          {example && <span className="mt-1 block text-xs text-muted-foreground">{example}</span>}
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-4 font-medium text-foreground">{annualFeeLabel(card)}</td>
                    <td className="px-4 py-4 text-foreground">{rate}</td>
                    <td className="px-4 py-4">
                      {highlights.length > 0 ? (
                        <ul className="space-y-1.5">
                          {highlights.map((highlight) => (
                            <li key={highlight} className="flex gap-2 text-foreground">
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                              <span>{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      ) : <span className="text-muted-foreground">Keine Angabe</span>}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex flex-col items-center gap-1.5">
                        {affiliateUrl ? (
                          <Button
                            size="sm"
                            className="min-w-[150px] gap-2"
                            onClick={async () => {
                              trackAffiliateClick(card.id, "table_view", card.display_name || card.name);
                              await trackAffiliateClickGTM({
                                cardId: card.id,
                                cardName: card.display_name || card.name,
                                issuerName: card.issuer || "",
                                affiliateUrl,
                                clickSource: "table_view",
                                offerTitle: offer?.title,
                                annualFee: card.annual_fee ?? undefined,
                                effectiveRate: hasApplicableApr ? card.apr_effective : undefined,
                              });
                              handleAffiliateRedirect({
                                cardId: card.id,
                                cardName: card.display_name || card.name,
                                issuerName: card.issuer || "",
                                affiliateUrl,
                                source: "table_view",
                                offerTitle: offer?.title,
                                annualFee: card.annual_fee ?? undefined,
                                effectiveRate: hasApplicableApr ? card.apr_effective : undefined,
                              });
                            }}
                          >
                            <span className="max-w-[120px] truncate">{offer?.title || "Jetzt beantragen"}</span>
                            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="min-w-[150px] gap-2" asChild>
                            <Link to={`/kreditkarten/${card.slug}`}>
                              Details
                              <ArrowRight className="h-4 w-4" aria-hidden="true" />
                            </Link>
                          </Button>
                        )}
                        {sponsoredIds.has(card.id) && <span className="text-xs font-medium text-primary">Anzeige</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}